'use client';

import React, { useState, useEffect } from 'react';
import {
  MapPin, BedDouble, Bath, Maximize2, CheckCircle, Calendar, Car, Zap,
  ChevronLeft, ChevronRight, Lock, Phone, MessageCircle, Mail, Flag, X, Share2, Loader2, Bookmark, IndianRupee,
} from 'lucide-react';
import AppImage from '@/components/ui/AppImage';
import { Property } from '@/lib/mockProperties';
import { reportService } from '@/lib/services/dataService';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';

interface PropertyCardProps {
  property: Property;
  onContact: (p: Property) => void;
  onViewDetail: (p: Property) => void;
  isLocked?: boolean;
}

interface UnlockedDetails {
  ownerName: string;
  ownerPhone: string;
  ownerEmail: string;
  address: string;
  googleMapsLink: string;
  landmarks: string;
  locality: string;
  city: string;
  bhk: string;
  bathrooms: number | null;
  builtUpArea: number | null;
  furnishing: string;
  houseType: string;
  parking: boolean;
  powerBackup: boolean;
  availableFrom: string;
  listingNumber: string;
}

function formatRent(amount: number): string {
  if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
  if (amount >= 1000) return `₹${(amount / 1000).toFixed(0)}K`;
  return `₹${amount}`;
}

function getContactClicks(listingId: string): number {
  if (typeof window === 'undefined') return 0;
  return parseInt(localStorage.getItem(`contact_clicks_${listingId}`) || '0', 10);
}

function incrementContactClicks(listingId: string): number {
  if (typeof window === 'undefined') return 0;
  const current = getContactClicks(listingId) + 1;
  localStorage.setItem(`contact_clicks_${listingId}`, String(current));
  return current;
}

export default function PropertyCard({ property, onContact, onViewDetail, isLocked = false }: PropertyCardProps) {
  const [imgIndex, setImgIndex] = useState(0);
  const [unlockState, setUnlockState] = useState<'locked' | 'ad' | 'unlocked'>('locked');
  const [adCountdown, setAdCountdown] = useState(5);
  const [unlockedDetails, setUnlockedDetails] = useState<UnlockedDetails | null>(null);
  const [fetchingDetails, setFetchingDetails] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportSent, setReportSent] = useState(false);
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [shareToastVisible, setShareToastVisible] = useState(false);
  const [houseTakenModal, setHouseTakenModal] = useState(false);
  const [houseTakenAnswer, setHouseTakenAnswer] = useState<'yes' | 'no' | null>(null);
  const [saved, setSaved] = useState(false);
  const [savingListing, setSavingListing] = useState(false);
  const { user } = useAuth();
  const supabase = createClient();

  // Check if listing is already saved
  useEffect(() => {
    if (!user) return;
    supabase
      .from('saved_listings')
      .select('id')
      .eq('user_id', user.id)
      .eq('listing_id', property.id)
      .maybeSingle()
      .then(({ data }) => {
        setSaved(!!data);
      });
  }, [user, property.id]);

  const handleSaveToggle = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user) {
      window.location.href = '/sign-up-login-screen?tab=login';
      return;
    }
    setSavingListing(true);
    try {
      if (saved) {
        await supabase
          .from('saved_listings')
          .delete()
          .eq('user_id', user.id)
          .eq('listing_id', property.id);
        setSaved(false);
      } else {
        await supabase
          .from('saved_listings')
          .insert({ user_id: user.id, listing_id: property.id });
        setSaved(true);
      }
    } catch {
      // silent
    } finally {
      setSavingListing(false);
    }
  };

  const nextImg = (e: React.MouseEvent) => {
    e.stopPropagation();
    setImgIndex((prev) => (prev + 1) % property.photos.length);
  };
  const prevImg = (e: React.MouseEvent) => {
    e.stopPropagation();
    setImgIndex((prev) => (prev - 1 + property.photos.length) % property.photos.length);
  };

  // Fetch ALL protected property details from server after unlock
  const fetchPropertyDetails = async () => {
    setFetchingDetails(true);
    try {
      const res = await fetch(`/api/property/contact-details?listing_id=${property.id}`);
      if (res.ok) {
        const data: UnlockedDetails = await res.json();
        setUnlockedDetails(data);
      }
    } catch {
      // silent
    } finally {
      setFetchingDetails(false);
    }
  };

  const handleUnlock = (e: React.MouseEvent) => {
    e.stopPropagation();
    setUnlockState('ad');
    setAdCountdown(5);
    const interval = setInterval(() => {
      setAdCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setUnlockState('unlocked');
          fetchPropertyDetails();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleReport = (e: React.MouseEvent) => {
    e.stopPropagation();
    setReportOpen(true);
  };

  const handleShare = (e: React.MouseEvent) => {
    e.stopPropagation();
    const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || window.location.origin;
    const shareUrl = `${siteUrl}/?listing=${property.id}`;
    const listingBadge = property.listingNumber ? `[#${property.listingNumber}] ` : '';
    const shareTitle = `${listingBadge}${property.title}`;
    const shareText = `${listingBadge}${property.title} in ${property.locality}, ${property.city} — ₹${property.monthlyRent}/month`;

    const showCopied = () => {
      setShareCopied(true);
      setShareToastVisible(true);
      setTimeout(() => {
        setShareCopied(false);
        setShareToastVisible(false);
      }, 2500);
    };

    if (navigator.share) {
      navigator.share({ title: shareTitle, text: shareText, url: shareUrl }).catch(() => {});
    } else if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(shareUrl).then(showCopied).catch(() => {
        try {
          const ta = document.createElement('textarea');
          ta.value = shareUrl;
          ta.style.position = 'fixed';
          ta.style.opacity = '0';
          document.body.appendChild(ta);
          ta.focus();
          ta.select();
          document.execCommand('copy');
          document.body.removeChild(ta);
          showCopied();
        } catch { /* silent */ }
      });
    } else {
      try {
        const ta = document.createElement('textarea');
        ta.value = shareUrl;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        showCopied();
      } catch { /* silent */ }
    }
  };

  const handleContactClick = () => {
    const clicks = incrementContactClicks(property.id);
    if (clicks > 0 && clicks % 5 === 0) {
      setHouseTakenModal(true);
    }
  };

  const submitReport = async () => {
    if (!reportReason.trim()) return;
    setReportSubmitting(true);
    try {
      await reportService.createReport({
        listing_id: property.id,
        reporter_id: user?.id || null,
        reporter_name: user?.user_metadata?.full_name || user?.email || 'Anonymous',
        reason: reportReason,
        status: 'Open',
      });
    } catch {
      // silent
    } finally {
      setReportSubmitting(false);
      setReportSent(true);
      setTimeout(() => { setReportOpen(false); setReportSent(false); setReportReason(''); }, 1500);
    }
  };

  const whatsappMsg = unlockedDetails
    ? encodeURIComponent(`Hi, I'm interested in your property: ${property.title} listed on tolettu.com. Is it still available?`)
    : '';

  // Determine if this card is in the "unlocked" display state
  const isUnlocked = unlockState === 'unlocked';

  return (
    <>
      <div
        className="bg-card rounded-2xl border border-border card-shadow hover:card-shadow-hover transition-all duration-200 overflow-hidden group cursor-pointer"
        onClick={() => onViewDetail(property)}
      >
        {/* ── Image Gallery ─────────────────────────────────────────────── */}
        <div className="relative h-52 overflow-hidden bg-muted">
          <AppImage
            src={property.photos[imgIndex]}
            alt={`${property.title} - photo ${imgIndex + 1} of ${property.photos.length}`}
            fill
            className="object-cover property-card-img"
            sizes="(max-width: 640px) 100vw, (max-width: 1280px) 50vw, 33vw"
          />

          {/* Pay-per-view lock overlay */}
          {isLocked && (
            <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center gap-2">
              <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                <Lock size={20} className="text-white" />
              </div>
              <div className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-primary text-primary-foreground text-xs font-bold">
                <IndianRupee size={11} />29 to unlock
              </div>
            </div>
          )}

          {/* Image navigation arrows — always visible when multiple photos */}
          {property.photos.length > 1 && (
            <>
              <button
                type="button"
                onClick={prevImg}
                className="absolute left-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-black/50 text-white flex items-center justify-center z-10 opacity-70 group-hover:opacity-100 transition-opacity hover:bg-black/70"
                aria-label="Previous photo"
              >
                <ChevronLeft size={14} />
              </button>
              <button
                type="button"
                onClick={nextImg}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-black/50 text-white flex items-center justify-center z-10 opacity-70 group-hover:opacity-100 transition-opacity hover:bg-black/70"
                aria-label="Next photo"
              >
                <ChevronRight size={14} />
              </button>
              {/* Dot indicators */}
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1 z-10">
                {property.photos.map((_, i) => (
                  <span
                    key={`dot-${property.id}-${i}`}
                    className={`w-1.5 h-1.5 rounded-full transition-all ${i === imgIndex ? 'bg-white w-3' : 'bg-white/60'}`}
                  />
                ))}
              </div>
              {/* Image counter */}
              <div className="absolute bottom-2 right-2 z-10 bg-black/50 text-white text-xs px-1.5 py-0.5 rounded-full">
                {imgIndex + 1}/{property.photos.length}
              </div>
            </>
          )}

          {/* Verified / Featured badges */}
          <div className="absolute top-3 left-3 flex gap-1.5 flex-wrap">
            {property.verified && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-600 text-white">
                <CheckCircle size={10} />Verified
              </span>
            )}
            {property.featured && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-accent text-white">
                Featured
              </span>
            )}
          </div>

          {/* Save / Share buttons on image (only when not pay-locked) */}
          {!isLocked && (
            <div className="absolute top-3 right-3 flex items-center gap-1.5">
              <button
                onClick={handleSaveToggle}
                disabled={savingListing}
                className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors ${saved ? 'bg-primary text-primary-foreground' : 'bg-black/50 text-white hover:bg-black/70'}`}
                aria-label={saved ? `Remove ${property.title} from saved` : `Save ${property.title} for later`}
              >
                {savingListing ? <Loader2 size={13} className="animate-spin" /> : <Bookmark size={13} className={saved ? 'fill-current' : ''} />}
              </button>
              <button
                onClick={handleShare}
                className="w-7 h-7 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors"
                aria-label={`Share ${property.title}`}
              >
                {shareCopied ? <CheckCircle size={13} className="text-green-400" /> : <Share2 size={13} />}
              </button>
            </div>
          )}
        </div>

        {/* ── Card Body ─────────────────────────────────────────────────── */}
        <div className="p-4">
          {/* Title + Rent */}
          <div className="flex items-start justify-between gap-2 mb-2">
            <h3 className="text-sm font-bold text-foreground leading-snug line-clamp-2 flex-1">
              {property.title}
            </h3>
            <div className="text-right shrink-0">
              <div className="text-lg font-extrabold text-primary tabular-nums">{formatRent(property.monthlyRent)}</div>
              <div className="text-xs text-muted-foreground">/month</div>
            </div>
          </div>

          {/* Location — icon only when locked, full address when unlocked */}
          <div className="flex items-center gap-1.5 mb-3">
            <MapPin size={12} className="text-muted-foreground shrink-0" />
            {isUnlocked && unlockedDetails ? (
              <span className="text-xs text-muted-foreground truncate">
                {unlockedDetails.locality || property.locality}, {unlockedDetails.city || property.city}
              </span>
            ) : (
              /* Locked: show only the icon, no text, no placeholder */
              <span className="text-xs text-muted-foreground">Location hidden</span>
            )}
          </div>

          {/* ── UNLOCKED: full property details ─────────────────────────── */}
          {isUnlocked && (
            <>
              {unlockedDetails?.listingNumber && (
                <div className="flex items-center gap-1 mb-3">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-mono font-semibold bg-primary/10 text-primary border border-primary/20">
                    {unlockedDetails.listingNumber}
                  </span>
                </div>
              )}

              {(unlockedDetails?.bhk || unlockedDetails?.bathrooms || unlockedDetails?.builtUpArea) && (
                <div className="flex items-center gap-3 text-xs text-muted-foreground mb-3">
                  {unlockedDetails.bhk && (
                    <span className="flex items-center gap-1"><BedDouble size={12} />{unlockedDetails.bhk}</span>
                  )}
                  {unlockedDetails.bhk && unlockedDetails.bathrooms && <span className="w-px h-3 bg-border" />}
                  {unlockedDetails.bathrooms != null && (
                    <span className="flex items-center gap-1"><Bath size={12} />{unlockedDetails.bathrooms} Bath</span>
                  )}
                  {unlockedDetails.bathrooms != null && unlockedDetails.builtUpArea && <span className="w-px h-3 bg-border" />}
                  {unlockedDetails.builtUpArea != null && unlockedDetails.builtUpArea > 0 && (
                    <span className="flex items-center gap-1"><Maximize2 size={12} />{unlockedDetails.builtUpArea} sq.ft</span>
                  )}
                </div>
              )}

              {(unlockedDetails?.furnishing || unlockedDetails?.houseType || unlockedDetails?.parking || unlockedDetails?.powerBackup) && (
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {unlockedDetails.furnishing && (
                    <span className="px-2 py-0.5 rounded-lg text-xs font-medium bg-secondary text-secondary-foreground">{unlockedDetails.furnishing}</span>
                  )}
                  {unlockedDetails.houseType && (
                    <span className="px-2 py-0.5 rounded-lg text-xs font-medium bg-muted text-muted-foreground">{unlockedDetails.houseType}</span>
                  )}
                  {unlockedDetails.parking && (
                    <span className="px-2 py-0.5 rounded-lg text-xs font-medium bg-muted text-muted-foreground flex items-center gap-1"><Car size={10} />Parking</span>
                  )}
                  {unlockedDetails.powerBackup && (
                    <span className="px-2 py-0.5 rounded-lg text-xs font-medium bg-muted text-muted-foreground flex items-center gap-1"><Zap size={10} />Power Backup</span>
                  )}
                </div>
              )}

              {unlockedDetails?.availableFrom && (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-3">
                  <Calendar size={12} />
                  <span>Available from {unlockedDetails.availableFrom}</span>
                </div>
              )}

              {unlockedDetails?.address && (
                <div className="flex items-start gap-1.5 px-2 py-1.5 rounded-lg bg-muted/60 text-xs text-foreground mb-3">
                  <MapPin size={11} className="text-primary shrink-0 mt-0.5" />
                  <span>{unlockedDetails.address}</span>
                </div>
              )}
            </>
          )}

          {/* ── Action buttons ────────────────────────────────────────────── */}
          {isLocked ? (
            /* Pay-per-view locked: open PaywallModal */
            <button
              onClick={(e) => { e.stopPropagation(); onViewDetail(property); }}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-bold hover:opacity-90 transition-all"
            >
              <Lock size={14} />
              🔒 Unlock Property Details
            </button>
          ) : (
            <div onClick={(e) => e.stopPropagation()}>
              {/* ── LOCKED state (ad-based unlock) ── */}
              {unlockState === 'locked' && (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <button
                      onClick={handleShare}
                      className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-xl text-xs font-semibold text-muted-foreground hover:text-primary hover:bg-primary/5 border border-border hover:border-primary/30 transition-all duration-150"
                      aria-label={`Share ${property.title}`}
                    >
                      {shareCopied ? <CheckCircle size={12} className="text-green-600" /> : <Share2 size={12} />}
                      {shareCopied ? 'Link Copied!' : 'Share'}
                    </button>
                    <button
                      onClick={handleReport}
                      className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-xl text-xs font-semibold text-muted-foreground hover:text-red-500 hover:bg-red-50 border border-border hover:border-red-200 transition-all duration-150"
                      aria-label={`Report listing ${property.title}`}
                    >
                      <Flag size={12} />
                      Report
                    </button>
                  </div>
                  <button
                    onClick={handleUnlock}
                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-bold hover:opacity-90 transition-all"
                  >
                    <Lock size={14} />
                    🔒 Unlock Property Details
                  </button>
                </div>
              )}

              {/* ── AD countdown ── */}
              {unlockState === 'ad' && (
                <div className="rounded-xl bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 p-3 text-center">
                  <div className="text-xs font-bold text-amber-700 mb-1.5">📢 Advertisement</div>
                  <div className="text-sm font-semibold text-foreground mb-1">QuickMove Packers &amp; Movers</div>
                  <div className="text-xs text-muted-foreground mb-2">Trusted relocation services across India. Get 20% off on first booking!</div>
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-bold">
                    <span className="w-4 h-4 rounded-full bg-amber-600 text-white flex items-center justify-center text-xs font-extrabold">{adCountdown}</span>
                    Property details unlock in {adCountdown}s
                  </div>
                </div>
              )}

              {/* ── UNLOCKED state: contact buttons ── */}
              {unlockState === 'unlocked' && (
                <div className="space-y-2">
                  {fetchingDetails ? (
                    <div className="flex items-center justify-center gap-2 py-3 text-xs text-muted-foreground">
                      <Loader2 size={14} className="animate-spin" />
                      Loading property details...
                    </div>
                  ) : unlockedDetails ? (
                    <>
                      <div className="text-xs text-center text-green-600 font-semibold mb-2 flex items-center justify-center gap-1">
                        <CheckCircle size={12} />✓ Property Details Unlocked
                      </div>
                      {(unlockedDetails.ownerPhone || unlockedDetails.ownerEmail) && (
                        <div className="grid grid-cols-3 gap-2">
                          {unlockedDetails.ownerPhone && (
                            <a
                              href={`tel:${unlockedDetails.ownerPhone}`}
                              onClick={(e) => { e.stopPropagation(); handleContactClick(); }}
                              className="flex flex-col items-center gap-1 py-2 rounded-xl bg-primary text-primary-foreground text-xs font-semibold hover:opacity-90 active:scale-95 transition-all duration-150"
                              aria-label={`Call owner of ${property.title}`}
                            >
                              <Phone size={14} />Call
                            </a>
                          )}
                          {unlockedDetails.ownerPhone && (
                            <a
                              href={`https://wa.me/91${unlockedDetails.ownerPhone.replace(/\D/g, '')}?text=${whatsappMsg}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => { e.stopPropagation(); handleContactClick(); }}
                              className="flex flex-col items-center gap-1 py-2 rounded-xl bg-green-600 text-white text-xs font-semibold hover:bg-green-700 active:scale-95 transition-all duration-150"
                              aria-label={`WhatsApp owner of ${property.title}`}
                            >
                              <MessageCircle size={14} />WhatsApp
                            </a>
                          )}
                          {unlockedDetails.ownerEmail && (
                            <a
                              href={`mailto:${unlockedDetails.ownerEmail}?subject=Inquiry about ${property.title}&body=Hi, I'm interested in your property listed on tolettu.com.`}
                              onClick={(e) => { e.stopPropagation(); handleContactClick(); }}
                              className="flex flex-col items-center gap-1 py-2 rounded-xl bg-muted text-foreground text-xs font-semibold hover:bg-border active:scale-95 transition-all duration-150"
                              aria-label={`Email owner of ${property.title}`}
                            >
                              <Mail size={14} />Email
                            </a>
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-xs text-center text-muted-foreground py-2">
                      Could not load property details. Please try again.
                    </div>
                  )}
                  <div className="flex gap-2">
                    <button
                      onClick={handleShare}
                      className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-xl text-xs font-semibold text-muted-foreground hover:text-primary hover:bg-primary/5 border border-border hover:border-primary/30 transition-all duration-150"
                      aria-label={`Share ${property.title}`}
                    >
                      {shareCopied ? <CheckCircle size={12} className="text-green-600" /> : <Share2 size={12} />}
                      {shareCopied ? 'Link Copied!' : 'Share'}
                    </button>
                    <button
                      onClick={handleReport}
                      className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-xl text-xs font-semibold text-muted-foreground hover:text-red-500 hover:bg-red-50 border border-border hover:border-red-200 transition-all duration-150"
                      aria-label={`Report listing ${property.title}`}
                    >
                      <Flag size={12} />Report
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Link Copied Toast ─────────────────────────────────────────────── */}
      {shareToastVisible && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[9999] pointer-events-none">
          <div className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-gray-900 text-white text-sm font-semibold shadow-lg">
            <CheckCircle size={15} className="text-green-400" />
            Link copied!
          </div>
        </div>
      )}

      {/* ── Report Modal ──────────────────────────────────────────────────── */}
      {reportOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setReportOpen(false)}>
          <div className="absolute inset-0 bg-black/50" />
          <div className="relative bg-card rounded-2xl shadow-modal w-full max-w-sm p-6 fade-in" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-xl bg-red-100 flex items-center justify-center">
                  <Flag size={16} className="text-red-600" />
                </div>
                <h3 className="text-base font-extrabold text-foreground">Report Listing</h3>
              </div>
              <button onClick={() => setReportOpen(false)} className="p-1.5 rounded-lg hover:bg-muted" aria-label="Close">
                <X size={18} />
              </button>
            </div>
            <p className="text-xs text-muted-foreground mb-3 line-clamp-2">{property.title}</p>
            {reportSent ? (
              <div className="flex items-center gap-2 text-green-700 bg-green-50 border border-green-200 rounded-xl px-4 py-3 text-sm font-semibold">
                <CheckCircle size={16} />Report submitted. Thank you!
              </div>
            ) : (
              <>
                <div className="flex flex-col gap-2 mb-4">
                  {['Fake or misleading listing', 'Wrong location or address', 'Inappropriate content', 'Already rented out', 'Spam or duplicate', 'Other'].map((reason) => (
                    <label key={reason} className="flex items-center gap-2.5 cursor-pointer group">
                      <input
                        type="radio"
                        name={`report-${property.id}`}
                        value={reason}
                        checked={reportReason === reason}
                        onChange={() => setReportReason(reason)}
                        className="accent-primary w-4 h-4"
                      />
                      <span className="text-sm text-foreground group-hover:text-primary transition-colors">{reason}</span>
                    </label>
                  ))}
                </div>
                <button
                  onClick={submitReport}
                  disabled={!reportReason || reportSubmitting}
                  className="btn-primary w-full justify-center"
                >
                  {reportSubmitting ? <><Loader2 size={14} className="animate-spin" />Submitting...</> : 'Submit Report'}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── "Has your house been taken?" Modal ───────────────────────────── */}
      {houseTakenModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setHouseTakenModal(false)}>
          <div className="absolute inset-0 bg-black/50" />
          <div className="relative bg-card rounded-2xl shadow-modal w-full max-w-sm p-6 fade-in" onClick={(e) => e.stopPropagation()}>
            {houseTakenAnswer === null ? (
              <>
                <div className="text-center mb-5">
                  <div className="w-14 h-14 rounded-2xl bg-amber-100 flex items-center justify-center mx-auto mb-3">
                    <span className="text-2xl">🏠</span>
                  </div>
                  <h3 className="text-base font-extrabold text-foreground mb-1">Has your house been taken?</h3>
                  <p className="text-xs text-muted-foreground">We noticed multiple people are contacting about this listing. Let us know if it&apos;s still available.</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setHouseTakenAnswer('yes')}
                    className="py-3 rounded-xl bg-green-600 text-white text-sm font-bold hover:bg-green-700 transition-colors"
                  >
                    ✅ Yes, Rented Out
                  </button>
                  <button
                    onClick={() => setHouseTakenAnswer('no')}
                    className="py-3 rounded-xl bg-primary text-primary-foreground text-sm font-bold hover:opacity-90 transition-colors"
                  >
                    ❌ No, Still Available
                  </button>
                </div>
              </>
            ) : (
              <div className="text-center py-4">
                <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
                  <CheckCircle size={28} className="text-green-600" />
                </div>
                <h3 className="text-base font-extrabold text-foreground mb-1">
                  {houseTakenAnswer === 'yes' ? 'Thanks for letting us know!' : 'Great! Listing stays active.'}
                </h3>
                <p className="text-xs text-muted-foreground mb-4">
                  {houseTakenAnswer === 'yes' ?'We will mark this listing as rented out. Thank you!' :'Your listing will continue to be shown to interested tenants.'}
                </p>
                <button
                  onClick={() => { setHouseTakenModal(false); setHouseTakenAnswer(null); }}
                  className="btn-primary justify-center"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}